import { createAdminSession, readAdminError } from './admin-session';
import { subscriberAdminStrings } from './subscriber-admin-strings';

type SubscriberStatus = 'Pending' | 'Confirmed';

type Subscriber = {
    id: number;
    emailAddress: string;
    status: SubscriberStatus;
    createdDate: string;
    subscribedDate: string | null;
};

type SubscriberPage = {
    subscribers: Subscriber[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

const statusSurface = requiredElement<HTMLElement>('status');
const subscribersSurface = requiredElement<HTMLElement>('subscribers');
const searchInput = requiredElement<HTMLInputElement>('subscriber-search');
const statusFilter = requiredElement<HTMLSelectElement>('subscriber-status-filter');
const createButton = requiredElement<HTMLButtonElement>('create-subscriber-button');
const previousPageButton = requiredElement<HTMLButtonElement>('previous-page-button');
const nextPageButton = requiredElement<HTMLButtonElement>('next-page-button');
const pageSummary = requiredElement<HTMLElement>('page-summary');
const modal = requiredElement<HTMLElement>('subscriber-modal');
const modalHeading = requiredElement<HTMLElement>('subscriber-modal-heading');
const closeModalButton = requiredElement<HTMLButtonElement>('close-subscriber-modal');
const form = requiredElement<HTMLFormElement>('subscriber-form');
const subscriberId = requiredElement<HTMLInputElement>('subscriber-id');
const emailInput = requiredElement<HTMLInputElement>('subscriber-email');
const statusField = requiredElement<HTMLElement>('subscriber-status-field');
const createStatus = requiredElement<HTMLSelectElement>('subscriber-create-status');
const saveButton = requiredElement<HTMLButtonElement>('save-subscriber-button');
let currentPage = 1;
let totalPages = 0;
let searchTimer: number | undefined;
let restoreFocusElement: HTMLElement | null = null;

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

const adminSession = createAdminSession({
    signedOutMessage: subscriberAdminStrings.signedOut,
    loginFailedMessage: subscriberAdminStrings.loginFailed,
    requestFailedMessage: subscriberAdminStrings.requestFailed,
    setStatus,
    onAuthenticated: loadSubscribers,
    onSignedOut: () => {
        closeModal(false);
        subscribersSurface.replaceChildren();
        pageSummary.textContent = '';
    }
});

async function loadSubscribers(): Promise<void> {
    setStatus(subscriberAdminStrings.loading);
    const parameters = new URLSearchParams({
        search: searchInput.value.trim(),
        status: statusFilter.value,
        page: currentPage.toString(),
        pageSize: '25'
    });
    const response = await adminSession.request(`/api/admin/subscribers?${parameters}`);
    if (!response.ok) throw new Error(await readAdminError(response, subscriberAdminStrings.requestFailed));
    const page = await response.json() as SubscriberPage;
    currentPage = page.page;
    totalPages = page.totalPages;
    renderSubscribers(page.subscribers);
    pageSummary.textContent = subscriberAdminStrings.pageSummary(page.page, page.totalPages, page.totalCount);
    previousPageButton.disabled = page.page <= 1;
    nextPageButton.disabled = page.totalPages === 0 || page.page >= page.totalPages;
    setStatus(page.subscribers.length === 0 ? subscriberAdminStrings.noSubscribers : '');
}

function renderSubscribers(subscribers: Subscriber[]): void {
    subscribersSurface.replaceChildren(...subscribers.map(createSubscriberRow));
}

function createSubscriberRow(subscriber: Subscriber): HTMLElement {
    const row = document.createElement('article');
    row.className = 'subscriber-row';

    const identity = document.createElement('div');
    identity.className = 'subscriber-identity';
    const emailAddress = document.createElement('strong');
    emailAddress.textContent = subscriber.emailAddress;
    const dates = document.createElement('span');
    dates.className = 'subscriber-dates';
    dates.textContent = subscriber.status === 'Confirmed' && subscriber.subscribedDate != null
        ? `Added ${formatDate(subscriber.createdDate)} Confirmed ${formatDate(subscriber.subscribedDate)}`
        : `Added ${formatDate(subscriber.createdDate)}`;
    identity.append(emailAddress, dates);

    const status = document.createElement('span');
    status.className = `subscriber-badge ${subscriber.status.toLowerCase()}`;
    status.textContent = subscriber.status;

    const actions = document.createElement('div');
    actions.className = 'subscriber-actions';
    const editButton = createActionButton(subscriberAdminStrings.editEmail(subscriber.emailAddress), 'Edit');
    editButton.addEventListener('click', () => openEditModal(subscriber, editButton));
    actions.append(editButton);

    if (subscriber.status === 'Pending') {
        const confirmButton = createActionButton(subscriberAdminStrings.confirmEmail(subscriber.emailAddress), 'Confirm');
        confirmButton.addEventListener('click', () => void runButtonMutation(confirmButton, subscriberAdminStrings.confirmSucceeded, async () => {
            const response = await adminSession.request(`/api/admin/subscribers/${subscriber.id}/confirm`, { method: 'POST' });
            await ensureSuccess(response);
        }));
        actions.append(confirmButton);
    }

    const deleteButton = createActionButton(subscriberAdminStrings.deleteEmail(subscriber.emailAddress), 'Delete', 'danger');
    deleteButton.addEventListener('click', () => {
        if (!window.confirm(subscriberAdminStrings.confirmDelete(subscriber.emailAddress))) return;
        void runButtonMutation(deleteButton, subscriberAdminStrings.deleteSucceeded, async () => {
            const response = await adminSession.request(`/api/admin/subscribers/${subscriber.id}`, { method: 'DELETE' });
            await ensureSuccess(response);
        });
    });
    actions.append(deleteButton);
    row.append(identity, status, actions);
    return row;
}

function createActionButton(label: string, text: string, className = 'secondary'): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = text;
    button.setAttribute('aria-label', label);
    return button;
}

function formatDate(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

function openCreateModal(): void {
    form.reset();
    subscriberId.value = '';
    createStatus.value = 'confirmed';
    statusField.hidden = false;
    modalHeading.textContent = subscriberAdminStrings.addSubscriber;
    saveButton.textContent = subscriberAdminStrings.addSubscriber;
    openModal(createButton);
}

function openEditModal(subscriber: Subscriber, trigger: HTMLElement): void {
    form.reset();
    subscriberId.value = subscriber.id.toString();
    emailInput.value = subscriber.emailAddress;
    statusField.hidden = true;
    modalHeading.textContent = subscriberAdminStrings.editSubscriber;
    saveButton.textContent = subscriberAdminStrings.editSubscriber;
    openModal(trigger);
}

function openModal(trigger: HTMLElement): void {
    restoreFocusElement = trigger;
    modal.hidden = false;
    document.body.classList.add('modal-open');
    emailInput.focus();
    emailInput.select();
}

function closeModal(restoreFocus = true): void {
    if (modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('modal-open');
    if (restoreFocus) restoreFocusElement?.focus();
    restoreFocusElement = null;
}

async function ensureSuccess(response: Response): Promise<void> {
    if (!response.ok) throw new Error(await readAdminError(response, subscriberAdminStrings.requestFailed));
}

async function runButtonMutation(button: HTMLButtonElement, successMessage: string, action: () => Promise<void>): Promise<void> {
    button.disabled = true;
    try {
        await action();
        await loadSubscribers();
        setStatus(successMessage);
    } catch (error) {
        setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error');
    } finally {
        button.disabled = false;
    }
}

createButton.addEventListener('click', openCreateModal);
closeModalButton.addEventListener('click', () => closeModal());
modal.addEventListener('click', event => {
    if (event.target === modal) closeModal();
});
modal.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(modal.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled)'))
        .filter(element => !element.closest('[hidden]'));
    const first = focusable[0];
    const last = focusable.at(-1);
    if (first == null || last == null) return;
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
});

form.addEventListener('submit', event => {
    event.preventDefault();
    void (async () => {
        saveButton.disabled = true;
        try {
            const id = subscriberId.value;
            const creating = id.length === 0;
            const response = await adminSession.request(
                creating ? '/api/admin/subscribers' : `/api/admin/subscribers/${id}`,
                {
                    method: creating ? 'POST' : 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(creating
                        ? { emailAddress: emailInput.value, status: createStatus.value }
                        : { emailAddress: emailInput.value })
                });
            await ensureSuccess(response);
            closeModal();
            currentPage = 1;
            await loadSubscribers();
            setStatus(creating ? subscriberAdminStrings.addSucceeded : subscriberAdminStrings.updateSucceeded);
        } catch (error) {
            setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error');
        } finally {
            saveButton.disabled = false;
        }
    })();
});

searchInput.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
        currentPage = 1;
        void loadSubscribers().catch(error => setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error'));
    }, 300);
});
statusFilter.addEventListener('change', () => {
    currentPage = 1;
    void loadSubscribers().catch(error => setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error'));
});
previousPageButton.addEventListener('click', () => {
    if (currentPage <= 1) return;
    currentPage--;
    void loadSubscribers().catch(error => setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error'));
});
nextPageButton.addEventListener('click', () => {
    if (currentPage >= totalPages) return;
    currentPage++;
    void loadSubscribers().catch(error => setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error'));
});

void adminSession.initialize()
    .catch(error => setStatus(error instanceof Error ? error.message : subscriberAdminStrings.requestFailed, 'error'));
